import { backendClient } from '@/api/backendClient';
import type {
  ApiAccessManagementRequest,
  ApiLifeCycleRequest,
  ApiUpdateRequest,
  ApiVisibilityRequest,
  McpRequest,
  McpResponse,
  ScopeCreationRequest,
  ScopeDeletionRequest,
} from '@/openapi/schema';
import type { CreateMcpInput } from '@/pages/explorer/mcps/AddMcp/schema';

export async function updateMcpGuide(
  mcpSlug: string,
  environmentSlug: string,
  version: string,
  guide: string,
): Promise<void> {
  const { error, response } = await backendClient.PUT(
    '/mcp/{mcpSlug}/environments/{environmentSlug}/releases/{version}/guide',
    {
      params: { path: { mcpSlug, environmentSlug, version } },
      body: { guide },
    },
  );
  if (!response.ok) {
    throw new Error(error?.detail ?? `Failed to save the guide (${response.status}).`);
  }
}

export async function createMcp(appId: string, data: CreateMcpInput): Promise<McpResponse> {
  const {
    data: created,
    error,
    response,
  } = await backendClient.POST('/mcp', {
    body: {
      appId,
      slug: data.slug,
      name: data.name,
      classification: data.classification as McpRequest['classification'],
      stage: data.stage,
      description: data.description || null,
    },
  });
  if (!response.ok || !created) {
    throw new Error(error?.detail ?? `Failed to create the MCP server (${response.status}).`);
  }

  if (data.guide?.trim()) {
    try {
      await updateMcpGuide(created.slug, '@default', '@latest', data.guide);
    } catch {
      throw new Error('The MCP was created, but saving the guide failed. Retry from the MCP management page.');
    }
  }

  return created;
}

export function updateAccessManagement(apiSlug: string, request: ApiAccessManagementRequest) {
  return backendClient.PUT('/apis/{apiSlug}/access-management', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}

export function updateVisibilityConfig(apiSlug: string, request: ApiVisibilityRequest) {
  return backendClient.PUT('/apis/{apiSlug}/visibility', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}

export function updateApiLifeCycle(apiSlug: string, request: ApiLifeCycleRequest) {
  return backendClient.POST('/apis/{apiSlug}/life-cycle', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}

export function updateApiFields(apiSlug: string, request: ApiUpdateRequest) {
  return backendClient.PATCH('/apis/{apiSlug}', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}

export function createScopeCreationRequest(apiSlug: string, request: ScopeCreationRequest) {
  return backendClient.POST('/apis/{apiSlug}/scope-creation-requests', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}

export function deletePendingScopeCreationRequest(apiSlug: string, scopeCreationRequestId: string) {
  return backendClient.DELETE('/apis/{apiSlug}/scope-creation-requests/{scopeCreationRequestId}', {
    params: {
      path: {
        apiSlug,
        scopeCreationRequestId,
      },
    },
  });
}

export function createScopeDeletionRequest(apiSlug: string, request: ScopeDeletionRequest) {
  return backendClient.POST('/apis/{apiSlug}/scope-deletion-requests', {
    params: {
      path: {
        apiSlug,
      },
    },
    body: request,
  });
}
